import { useMutation } from "@apollo/client";
import { compact } from "lodash";
import moment from "moment";
import { ReactNode, useCallback, useContext, useMemo, useState } from "react";
import {
  AssessmentDeliveryState,
  AssessmentGroupDelivery,
  AssessmentQuestionType,
  AssessmentState,
  ComplianceProgramCalibrationStatus,
  ComplianceProgramCalibrationType,
  ComplianceProgramPerformanceReportRowFragment,
  ComplianceProgramReportPrevAnswerFragment,
  GetComplianceProgramForReportQuery,
  SaveAssessmentDeliveryMutation,
  SaveAssessmentDeliveryMutationVariables,
  UnsubmitAssessmentMutation,
  UnsubmitAssessmentMutationVariables,
  UpdateCalibrationStatusMutation,
  UpdateCalibrationStatusMutationVariables,
} from "types/graphql-schema";

import CalibrationChangelogModal from "@apps/assessments/components/calibration/calibration-changelog-modal";
import reopenAssessmentMutation from "@apps/assessments/graphql/reopen-assessment-mutation";
import updateAssessmentDeliveryMutation from "@apps/assessments/graphql/update-assessment-delivery-mutation";
import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import getComplianceProgramForReportQuery from "@apps/reporting/graphql/get-compliance-program-for-report-query";
import updateCalibrationStatusMutation from "@apps/reporting/graphql/update-calibration-status-mutation";
import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { removeConsecutiveDividers } from "@components/dropdown/dropdown-helpers";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import StatusPill, {
  StatusPillEnum,
} from "@components/status-pill/status-pill";
import { TableBodyCell, TableBodyRow } from "@components/table/table";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import {
  getCalibrateConfirmation,
  getCalibrateSuccess,
  getDeliveryStateChangeConfirmation,
  getDeliveryStateChangeSuccess,
  getReopenConfirmation,
  getReopenSuccess,
  getUndoDeliveryConfirmation,
  getUndoDeliverySuccess,
} from "./confirmation-strings";
import EmployeeDrawerSidebar from "./employee-drawer/employee-drawer-sidebar";
import { PerformanceAssessmentTableQuestion } from "./performance-assessment-table";
import PerformanceAssessmentTableEmptyCell from "./performance-assessment-table-empty-cell";
import PerformanceAssessmentTableNameCell from "./performance-assessment-table-name-cell";
import PerformanceAssessmentTableRowAnswerCell from "./performance-assessment-table-row-answer-cell";
import PerformanceAssessmentTablePrecalibrationCell from "./performance-assessment-table-row-precalibration-cell";
import { AssessmentReportTableColumn } from "./report-filters/types";
import usePrecalibrationData from "./use-precalibration-data";

const PerformanceAssessmentTableRow = ({
  reportRow,
  questions,
  assessmentTemplate,
  visibleTableColumns,
  onRowActionConfirm,
  recentAnswerColumns = [],
  prevAnswers,
}: {
  reportRow: ComplianceProgramPerformanceReportRowFragment & {
    calibrationGroupName?: string;
  };
  questions: PerformanceAssessmentTableQuestion[];
  assessmentTemplate: NonNullable<
    NonNullable<
      GetComplianceProgramForReportQuery["complianceProgram"]
    >["performanceAssessmentTemplate"]
  >;
  visibleTableColumns: AssessmentReportTableColumn[];
  recentAnswerColumns?: number[];
  prevAnswers: ComplianceProgramReportPrevAnswerFragment[];
  onRowActionConfirm: (message: ReactNode) => Promise<boolean>;
}) => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const currentUser = currentUserVar();
  const { complianceProgram, complianceProgramId } = useContext(
    PerformanceAssessmentReportContext,
  );

  const [isEmployeeDrawerOpen, setIsEmployeeDrawerOpen] = useState(false);
  const [isChangelogModalOpen, setIsChangelogModalOpen] = useState(false);

  const handleCloseChangelogModal = useCallback(() => {
    setIsChangelogModalOpen(false);
  }, []);

  const responder = useMemo(
    () =>
      reportRow.assessment
        ? assertNonNull(reportRow.assessment.responder)
        : reportRow.user.primaryManager,
    [reportRow.assessment, reportRow.user],
  );
  const target = useMemo(() => assertNonNull(reportRow.user), [reportRow.user]);
  const answers = useMemo(
    () =>
      reportRow.assessment ? compact([...reportRow.assessment.answers]) : [],
    [reportRow.assessment],
  );
  const answerIdByQuestionId = useMemo(() => {
    return answers.reduce(
      (acc, ans) => ({
        ...acc,
        [ans.questionId]: ans.id,
      }),
      {} as Record<number, number>,
    );
  }, [answers]);

  const { precalibrationData } = usePrecalibrationData({
    complianceProgramId,
    templateId: assessmentTemplate.id,
    skip:
      complianceProgram?.calibration !==
      ComplianceProgramCalibrationType.PreCalibration,
  });
  const rowHasCalibrations = useMemo(
    () =>
      (precalibrationData &&
        precalibrationData.filter(
          (calibration) => calibration.target?.id === reportRow.user.id,
        ).length > 0) ||
      answers.some(
        (answer) => answer.hasCalibrations || answer.hasCalibrationSuggestions,
      ),
    [answers, precalibrationData, reportRow],
  );

  const { hasPreCalibration, hasPostCalibration } = useMemo(
    () => ({
      hasPreCalibration:
        complianceProgram?.calibration ===
        ComplianceProgramCalibrationType.PreCalibration,
      hasPostCalibration:
        complianceProgram?.calibration ===
        ComplianceProgramCalibrationType.PostCalibration,
    }),
    [complianceProgram],
  );
  const assessmentDeliveryState = useMemo(() => {
    if (reportRow.assessment) {
      if (reportRow.assessment.assessmentDelivery) {
        return reportRow.assessment.assessmentDelivery.state;
      }
      return reportRow.assessment.assessmentDeliveryState;
    }
    return null;
  }, [reportRow.assessment]);

  const canUpdateDeliveryPackage = useMemo(
    () =>
      reportRow.assessment?.assessmentDelivery &&
      !!reportRow.assessment.assessmentDelivery.canUpdate?.permission,
    [reportRow.assessment],
  );
  const canReadAssessmentDelivery = useMemo(
    () =>
      reportRow.assessment?.assessmentDelivery &&
      !!reportRow.assessment.assessmentDelivery.canRead?.permission,
    [reportRow.assessment],
  );
  const isTargetManager = useMemo(
    () => target.primaryManager?.id === currentUser.id,
    [target, currentUser],
  );
  const [saveAssessmentDelivery] = useMutation<
    SaveAssessmentDeliveryMutation,
    SaveAssessmentDeliveryMutationVariables
  >(updateAssessmentDeliveryMutation);
  const [updateCalibrationStatus] = useMutation<
    UpdateCalibrationStatusMutation,
    UpdateCalibrationStatusMutationVariables
  >(updateCalibrationStatusMutation);

  const [reopenAssessment] = useMutation<
    UnsubmitAssessmentMutation,
    UnsubmitAssessmentMutationVariables
  >(reopenAssessmentMutation);

  const isAdminApprovalDelivery = useMemo(
    () => assessmentTemplate.delivery === AssessmentGroupDelivery.AdminApproval,
    [assessmentTemplate],
  );
  const isManagerApprovalDelivery = useMemo(
    () => assessmentTemplate.delivery === AssessmentGroupDelivery.Partial,
    [assessmentTemplate],
  );

  const handleChangeDeliveryState = useCallback(
    async (state: AssessmentDeliveryState) => {
      const confirmMsg = getDeliveryStateChangeConfirmation(state, {
        label,
        targetName: target.name,
        isTargetManager,
      });
      const successMsg = getDeliveryStateChangeSuccess(state, label);

      const confirmation = await onRowActionConfirm(confirmMsg);
      if (confirmation && reportRow.assessment?.assessmentDelivery) {
        saveAssessmentDelivery({
          variables: {
            assessmentDeliveryId: reportRow.assessment.assessmentDelivery.id,
            state,
          },
          onCompleted: () => {
            successNotificationVar({
              title: successMsg,
            });
          },
        });
      }
    },
    [
      onRowActionConfirm,
      reportRow.assessment,
      saveAssessmentDelivery,
      label,
      target.name,
      isTargetManager,
    ],
  );
  const handleClickNotStarted = useCallback(async () => {
    handleChangeDeliveryState(AssessmentDeliveryState.AwaitingAdminApproval);
  }, [handleChangeDeliveryState]);
  const handleClickApprove = useCallback(async () => {
    handleChangeDeliveryState(AssessmentDeliveryState.Draft);
  }, [handleChangeDeliveryState]);
  const handleClickUnCalibrate = useCallback(async () => {
    updateCalibrationStatus({
      variables: {
        complianceProgramId,
        targetId: target.id,
        status: ComplianceProgramCalibrationStatus.NotStarted,
      },
      onCompleted: () => {
        successNotificationVar({
          title: `${label("review", {
            capitalize: true,
          })} Package marked as Not Calibrated`,
        });
      },
    });
  }, [complianceProgramId, label, target, updateCalibrationStatus]);
  const handleClickCalibrate = useCallback(async () => {
    const confirmMsg = getCalibrateConfirmation({
      label,
      calibrationType: complianceProgram?.calibration,
    });
    const confirmation = await onRowActionConfirm(confirmMsg);
    if (confirmation) {
      updateCalibrationStatus({
        variables: {
          complianceProgramId,
          targetId: target.id,
          status: ComplianceProgramCalibrationStatus.Calibrated,
        },
        onCompleted: () => {
          const successMsg = getCalibrateSuccess({
            label,
            calibrationType: complianceProgram?.calibration,
            targetName: target.name,
          });
          successNotificationVar({
            title: successMsg,
          });
        },
      });
    }
  }, [
    complianceProgram,
    complianceProgramId,
    label,
    onRowActionConfirm,
    target,
    updateCalibrationStatus,
  ]);
  const handleClickDeliver = useCallback(async () => {
    handleChangeDeliveryState(AssessmentDeliveryState.Delivered);
  }, [handleChangeDeliveryState]);
  const handleClickUndoDeliver = useCallback(async () => {
    const confirmMsg = getUndoDeliveryConfirmation(target.name);
    const successMsg = getUndoDeliverySuccess(label);

    const confirmation = await onRowActionConfirm(confirmMsg);
    if (confirmation && reportRow.assessment?.assessmentDelivery) {
      saveAssessmentDelivery({
        variables: {
          assessmentDeliveryId: reportRow.assessment.assessmentDelivery.id,
          state: isAdminApprovalDelivery
            ? AssessmentDeliveryState.AwaitingAdminApproval
            : AssessmentDeliveryState.Draft,
        },
        onCompleted: () => {
          successNotificationVar({
            title: successMsg,
          });
        },
      });
    }
  }, [
    onRowActionConfirm,
    reportRow.assessment,
    saveAssessmentDelivery,
    target.name,
    label,
    isAdminApprovalDelivery,
  ]);

  const handleClickSendBackSubmissionToManager = useCallback(async () => {
    const confirmMsg = getReopenConfirmation(label);
    const confirmation = await onRowActionConfirm(confirmMsg);
    if (confirmation && reportRow.assessment) {
      reopenAssessment({
        variables: {
          assessmentIds: [reportRow.assessment.id],
        },
        onCompleted: () => {
          const successMsg = getReopenSuccess(label);
          successNotificationVar({
            title: successMsg,
          });
        },
        refetchQueries: [getComplianceProgramForReportQuery],
      });
    }
  }, [onRowActionConfirm, reportRow.assessment, reopenAssessment, label]);

  const handleClickOpenChangelogModal = useCallback(() => {
    setIsChangelogModalOpen(true);
  }, []);

  const handleClickOpenEmployeeDrawer = useCallback(() => {
    setIsEmployeeDrawerOpen(true);
  }, []);

  const handleCloseEmployeeDrawer = useCallback(() => {
    setIsEmployeeDrawerOpen(false);
  }, []);

  const actionDropdownOptions: DropdownOptionType[] = useMemo(() => {
    if (
      hasPreCalibration &&
      reportRow.canCalibrateAnswer.permission &&
      reportRow.calibrationStatus ===
        ComplianceProgramCalibrationStatus.NotStarted
    ) {
      return [
        {
          label: "Mark as Calibrated",
          onClick: handleClickCalibrate,
          type: "button",
        },
      ];
    }
    return removeConsecutiveDividers(
      compact([
        (isAdminApprovalDelivery || hasPostCalibration) &&
          (canUpdateDeliveryPackage ||
            reportRow.canCalibrateAnswer.permission) && {
            label: "Change Status to...",
            type: "submenu",
            options: compact([
              reportRow.canCalibrateAnswer.permission &&
                reportRow.calibrationStatus ===
                  ComplianceProgramCalibrationStatus.Calibrated && {
                  label: "Not Calibrated",
                  onClick: handleClickUnCalibrate,
                  type: "button",
                },
              reportRow.canCalibrateAnswer.permission &&
                reportRow.calibrationStatus ===
                  ComplianceProgramCalibrationStatus.NotStarted && {
                  label: "Calibrated",
                  onClick: handleClickCalibrate,
                  type: "button",
                },
              canUpdateDeliveryPackage &&
                assessmentDeliveryState !==
                  AssessmentDeliveryState.AwaitingAdminApproval && {
                  label: "Not Approved for Sharing",
                  onClick: handleClickNotStarted,
                  type: "button",
                },
              canUpdateDeliveryPackage &&
                assessmentDeliveryState !== AssessmentDeliveryState.Draft && {
                  label: "Approved for Sharing",
                  onClick: handleClickApprove,
                  type: "button",
                },
            ]),
          },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) &&
          canUpdateDeliveryPackage &&
          assessmentDeliveryState !== AssessmentDeliveryState.Delivered && {
            label: `Deliver this ${label("review", { capitalize: true })}`,
            onClick: handleClickDeliver,
            type: "button",
          },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) &&
          canUpdateDeliveryPackage &&
          assessmentDeliveryState === AssessmentDeliveryState.Delivered && {
            label: `Undo Delivery of this ${label("review", {
              capitalize: true,
            })} Package`,
            onClick: handleClickUndoDeliver,
            type: "button",
          },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) &&
          canUpdateDeliveryPackage &&
          isAdmin &&
          assessmentDeliveryState !== AssessmentDeliveryState.Delivered && {
            label: `Send Back to Manager`,
            onClick: handleClickSendBackSubmissionToManager,
            type: "button",
          },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) && {
          type: "divider",
        },
        (hasPreCalibration || hasPostCalibration) &&
          reportRow.assessment && {
            label: `View Change Log`,
            onClick: handleClickOpenChangelogModal,
            type: "button",
          },
        { type: "divider" },
        reportRow.assessment?.assessmentDelivery &&
          canReadAssessmentDelivery && {
            label: `View ${label("review", { capitalize: true })} Package`,
            onClick: handleClickOpenEmployeeDrawer,
            type: "button",
          },
      ]),
    );
  }, [
    isAdminApprovalDelivery,
    canUpdateDeliveryPackage,
    isAdmin,
    reportRow.calibrationStatus,
    reportRow.assessment,
    reportRow.canCalibrateAnswer.permission,
    handleClickUnCalibrate,
    handleClickCalibrate,
    assessmentDeliveryState,
    handleClickNotStarted,
    handleClickApprove,
    isManagerApprovalDelivery,
    label,
    handleClickDeliver,
    handleClickUndoDeliver,
    handleClickSendBackSubmissionToManager,
    hasPostCalibration,
    hasPreCalibration,
    handleClickOpenChangelogModal,
    canReadAssessmentDelivery,
    handleClickOpenEmployeeDrawer,
  ]);

  const columnContent = useMemo(() => {
    return visibleTableColumns.map((column) => {
      switch (column.id) {
        case "name":
          return (
            <PerformanceAssessmentTableNameCell
              key={column.id}
              isEmployeeDrawerOpen={isEmployeeDrawerOpen}
              onOpenEmployeeDrawer={handleClickOpenEmployeeDrawer}
              target={target}
              actionDropdown={
                actionDropdownOptions.length > 0 && (
                  <Dropdown
                    align="end"
                    options={actionDropdownOptions}
                    aria-label="Performance assessment report row dropdown"
                  />
                )
              }
              viewCalibrations={
                (hasPreCalibration || hasPostCalibration) &&
                rowHasCalibrations && (
                  <Button
                    className="shrink-0"
                    icon={TopicflowIconMap.userEdit}
                    theme={buttonTheme.iconGray}
                    tooltip="View the calibration comments and edits"
                    onClick={handleClickOpenChangelogModal}
                  />
                )
              }
            />
          );
        case "status":
          return isAdminApprovalDelivery ? (
            <TableBodyCell key={column.id}>
              <div className="min-w-48 flex flex-nowrap grow shrink-0 items-center justify-between gap-2">
                <Tooltip
                  text={
                    assessmentDeliveryState === AssessmentDeliveryState.Draft
                      ? `The ${label(
                          "review",
                        )} was approved by an admin. The manager can now share it with the subject.`
                      : assessmentDeliveryState ===
                          AssessmentDeliveryState.AwaitingAdminApproval
                        ? `The ${label(
                            "review",
                          )} is awaiting approval by an admin.`
                        : assessmentDeliveryState ===
                            AssessmentDeliveryState.Delivered
                          ? `The ${label("review")} was shared with the subject.`
                          : reportRow.assessment?.state ===
                              AssessmentState.Submitted
                            ? `The ${label("review")} was submitted by the manager.`
                            : assessmentDeliveryState
                              ? `The ${label("review")} is in draft state.`
                              : `The ${label("review")} hasn't been submitted yet.`
                  }
                >
                  <div>
                    <StatusPill
                      label={
                        assessmentDeliveryState ===
                        AssessmentDeliveryState.Draft
                          ? "Approved"
                          : assessmentDeliveryState ===
                              AssessmentDeliveryState.Delivered
                            ? "Shared"
                            : "Not Approved"
                      }
                      status={
                        assessmentDeliveryState ===
                        AssessmentDeliveryState.Draft
                          ? StatusPillEnum.InProgress
                          : assessmentDeliveryState ===
                              AssessmentDeliveryState.Delivered
                            ? StatusPillEnum.Complete
                            : StatusPillEnum.NotApplicable
                      }
                    />
                  </div>
                </Tooltip>
              </div>
            </TableBodyCell>
          ) : (
            <TableBodyCell key={column.id}>
              <div className="min-w-48 flex flex-nowrap grow shrink-0 items-center justify-between gap-2">
                <Tooltip
                  text={
                    assessmentDeliveryState === AssessmentDeliveryState.Draft
                      ? `The manager can now share the ${label(
                          "review",
                        )} with the subject.`
                      : assessmentDeliveryState ===
                          AssessmentDeliveryState.Delivered
                        ? `The ${label("review")} was shared with the subject.`
                        : `The ${label("review")} hasn't been submitted yet.`
                  }
                >
                  <div>
                    <StatusPill
                      label={
                        assessmentDeliveryState ===
                        AssessmentDeliveryState.Draft
                          ? "Not Shared"
                          : assessmentDeliveryState ===
                              AssessmentDeliveryState.Delivered
                            ? "Shared"
                            : "N/A"
                      }
                      status={
                        assessmentDeliveryState ===
                        AssessmentDeliveryState.Delivered
                          ? StatusPillEnum.Complete
                          : StatusPillEnum.NotApplicable
                      }
                    />
                  </div>
                </Tooltip>
              </div>
            </TableBodyCell>
          );
        case "calibration-status":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-32 flex">
                <StatusPill
                  label={
                    reportRow.calibrationStatus ===
                    ComplianceProgramCalibrationStatus.NotStarted
                      ? "Not Calibrated"
                      : "Calibrated"
                  }
                  status={
                    reportRow.calibrationStatus !==
                    ComplianceProgramCalibrationStatus.NotStarted
                      ? StatusPillEnum.Complete
                      : StatusPillEnum.NotApplicable
                  }
                />
              </div>
            </TableBodyCell>
          );
        case "manager-status":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-32 flex">
                <StatusPill
                  label={reportRow.assessment ? "Completed" : "In Progress"}
                  status={
                    reportRow.assessment
                      ? StatusPillEnum.Complete
                      : StatusPillEnum.InProgress
                  }
                />
              </div>
            </TableBodyCell>
          );
        case "enrollment-date":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {moment(reportRow.targetAddedToProgramOn).format("MMM D, YYYY")}
              </div>
            </TableBodyCell>
          );
        case "current-role-track":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {reportRow.roles
                  .map((role) => `${role.careerTrack.title}`)
                  .join(", ")}
              </div>
            </TableBodyCell>
          );
        case "current-role-level":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {reportRow.roles.map((role) => `${role.level}`).join(", ")}
              </div>
            </TableBodyCell>
          );
        case "current-role":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {reportRow.roles.map((role) => `${role.title}`).join(", ")}
              </div>
            </TableBodyCell>
          );
        case "manager":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">{responder?.name}</div>
            </TableBodyCell>
          );
        case "position":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">{reportRow.position}</div>
            </TableBodyCell>
          );
        // eslint-disable-next-line local-rules/use-label
        case "team":
          return (
            <TableBodyCell key={column.id}>
              <div className="min-w-36">
                {target.teamsList.map(({ title }) => title).join(", ")}
              </div>
            </TableBodyCell>
          );
        case "role-avg":
          return (
            <TableBodyCell key={column.id}>
              <div
                className={classNames(
                  "min-w-36",
                  !reportRow.assessment?.roleAssessmentAvg && "text-gray-300",
                )}
              >
                {reportRow.assessment
                  ? (reportRow.assessment.roleAssessmentAvg ?? "N/A")
                  : null}
              </div>
            </TableBodyCell>
          );
        case "calibration-group":
          return (
            <TableBodyCell key={column.id}>
              <div
                className={classNames(
                  "min-w-36",
                  !reportRow.calibrationGroupName && "text-gray-300",
                )}
              >
                {reportRow.calibrationGroupName
                  ? reportRow.calibrationGroupName
                  : "No Group"}
              </div>
            </TableBodyCell>
          );
        case "self-role-avg":
          return (
            <TableBodyCell key={column.id}>
              <div
                className={classNames(
                  "min-w-36",
                  !reportRow.assessment?.selfRoleAssessmentAvg &&
                    "text-gray-300",
                )}
              >
                {reportRow.assessment
                  ? (reportRow.assessment.selfRoleAssessmentAvg ?? "N/A")
                  : null}
              </div>
            </TableBodyCell>
          );
        default: {
          const question = questions.find((q) => q.id === column.id);
          const isCalibratableQuestionType =
            !!question &&
            (question.questionType === AssessmentQuestionType.Range ||
              question.questionType === AssessmentQuestionType.TalentIndicator);
          const showPrevAnswer = question
            ? recentAnswerColumns.includes(question.id)
            : false;
          if (question && reportRow.assessment) {
            return (
              <PerformanceAssessmentTableRowAnswerCell
                key={question.id}
                assessmentTemplate={assessmentTemplate}
                assessment={reportRow.assessment}
                question={question}
                answers={answers}
                prevAnswers={prevAnswers}
                programHasCalibration={hasPostCalibration || hasPreCalibration}
                canCalibrate={
                  reportRow.canCalibrateAnswer.permission &&
                  isCalibratableQuestionType
                }
                canSuggestCalibration={
                  reportRow.canSuggestAnswer.permission &&
                  isCalibratableQuestionType
                }
                showPrevAnswer={showPrevAnswer}
              />
            );
          } else if (question && !reportRow.assessment && hasPreCalibration) {
            return (
              <PerformanceAssessmentTablePrecalibrationCell
                key={question.id}
                assessmentTemplate={assessmentTemplate}
                target={reportRow.user}
                question={question}
                canCalibrate={
                  reportRow.canCalibrateAnswer.permission &&
                  reportRow.calibrationStatus ===
                    ComplianceProgramCalibrationStatus.NotStarted &&
                  isCalibratableQuestionType
                }
                canSuggestCalibration={
                  reportRow.canSuggestAnswer.permission &&
                  reportRow.calibrationStatus ===
                    ComplianceProgramCalibrationStatus.NotStarted &&
                  isCalibratableQuestionType
                }
              />
            );
          }
          return (
            <PerformanceAssessmentTableEmptyCell
              key={column.id}
              padLeft={showPrevAnswer}
            />
          );
        }
      }
    });
  }, [
    actionDropdownOptions,
    answers,
    prevAnswers,
    reportRow,
    assessmentDeliveryState,
    questions,
    responder,
    isAdminApprovalDelivery,
    hasPostCalibration,
    hasPreCalibration,
    target,
    visibleTableColumns,
    recentAnswerColumns,
    handleClickOpenEmployeeDrawer,
    handleClickOpenChangelogModal,
    isEmployeeDrawerOpen,
    label,
    rowHasCalibrations,
    assessmentTemplate,
  ]);

  return (
    <>
      {isChangelogModalOpen && (
        <CalibrationChangelogModal
          reviewExists={!!reportRow.assessment}
          answerIdByQuestionId={answerIdByQuestionId}
          templateId={assessmentTemplate.id}
          canCalibrate={reportRow.canCalibrateAnswer.permission}
          canSuggestCalibration={reportRow.canSuggestAnswer.permission}
          target={target}
          onOpenEmployeeDrawer={handleClickOpenEmployeeDrawer}
          onClose={handleCloseChangelogModal}
        />
      )}
      {isEmployeeDrawerOpen && (
        <EmployeeDrawerSidebar
          target={target}
          onClose={handleCloseEmployeeDrawer}
        />
      )}
      <TableBodyRow
        // needs 1px height for hoverable cells to take up the entire row height, not sure why
        className="h-[1px]"
        key={reportRow.id}
      >
        {columnContent}
      </TableBodyRow>
    </>
  );
};

export default PerformanceAssessmentTableRow;
